import {
    Chart,
    LineController,
    LineElement,
    PointElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import {color} from "../helpers/color.js";

Chart.register(
    LineController,
    LineElement,
    PointElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
    Filler
);

export const LIVE_CHART_COLORS = ['#ffb000', '#66d9ef', '#22c55e', '#ef4444', '#8b5cf6'];

const GENERATED_COLOR_HUE_START = 315;
const GOLDEN_ANGLE = 137.508;

const hslToHex = (hue, saturation, lightness) => {
    const normalizedSaturation = saturation / 100;
    const normalizedLightness = lightness / 100;
    const chroma = (1 - Math.abs(2 * normalizedLightness - 1)) * normalizedSaturation;
    const hueSection = hue / 60;
    const secondary = chroma * (1 - Math.abs(hueSection % 2 - 1));
    let channels;

    if (hueSection < 1) channels = [chroma, secondary, 0];
    else if (hueSection < 2) channels = [secondary, chroma, 0];
    else if (hueSection < 3) channels = [0, chroma, secondary];
    else if (hueSection < 4) channels = [0, secondary, chroma];
    else if (hueSection < 5) channels = [secondary, 0, chroma];
    else channels = [chroma, 0, secondary];

    const match = normalizedLightness - chroma / 2;
    return `#${channels.map(channel => Math.round((channel + match) * 255)
        .toString(16)
        .padStart(2, '0')).join('')}`;
};

export const getLiveChartColor = index => {
    if (index < LIVE_CHART_COLORS.length) return LIVE_CHART_COLORS[index];

    const generatedIndex = index - LIVE_CHART_COLORS.length;
    const hue = (GENERATED_COLOR_HUE_START + generatedIndex * GOLDEN_ANGLE) % 360;
    return hslToHex(hue, 78, 56);
};

export const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const isFiniteNumber = value => typeof value === 'number' && Number.isFinite(value);

export const findClosestDatasetIndex = (points, eventY) => {
    if (!isFiniteNumber(eventY)) return null;

    let closest = null;
    points.forEach(point => {
        if (!Number.isInteger(point.datasetIndex) || !isFiniteNumber(point.y)) return;

        const distance = Math.abs(point.y - eventY);
        if (
            closest === null ||
            distance < closest.distance ||
            (distance === closest.distance && point.datasetIndex < closest.datasetIndex)
        ) {
            closest = {datasetIndex: point.datasetIndex, distance};
        }
    });

    return closest?.datasetIndex ?? null;
};

export const selectPredictionPills = (
    items,
    touchDatasetIndex,
    predictionDatasetIndex,
    limit = 4
) => {
    const validItems = items.filter(item => isFiniteNumber(item.value));
    const selectedItems = [];

    const addByDatasetIndex = datasetIndex => {
        const item = validItems.find(candidate => candidate.datasetIndex === datasetIndex);
        if (item && !selectedItems.includes(item)) selectedItems.push(item);
    };

    addByDatasetIndex(predictionDatasetIndex);
    addByDatasetIndex(touchDatasetIndex);

    validItems
        .filter(item => !selectedItems.includes(item))
        .sort((first, second) =>
            second.value - first.value || first.datasetIndex - second.datasetIndex
        )
        .forEach(item => {
            if (selectedItems.length < limit) selectedItems.push(item);
        });

    return selectedItems.slice(0, limit).map(item => ({
        ...item,
        selected: item.datasetIndex === touchDatasetIndex,
        predictionSelected: item.datasetIndex === predictionDatasetIndex
    }));
};

const DEFAULT_TAG_STYLE = Object.freeze({
    height: 26,
    gap: 4,
    font: 'bold 13px Arial, sans-serif',
    paddingX: 10,
    radius: 7,
    outlineWidth: 2
});
const MOBILE_TAG_STYLE = Object.freeze({
    ...DEFAULT_TAG_STYLE,
    height: 22,
    gap: 3,
    font: 'bold 12px Arial, sans-serif',
    paddingX: 8,
    radius: 6
});
const TAG_COLUMN_GAP = 8;
const TAG_CHART_PADDING = 4;
const TAG_CURSOR_GAP = 12;
const MOBILE_PILL_QUERY = '(max-width: 767.98px)';
const SELECTED_PLUS_LEADERS_MODE = 'selected-plus-leaders';

const usesPredictionPillMode = chart =>
    chart.canvas?.getAttribute('data-live-chart-pill-mode') === SELECTED_PLUS_LEADERS_MODE;

const usesCompactPredictionPills = chart => {
    const compactViewport = typeof window.matchMedia === 'function' &&
        window.matchMedia(MOBILE_PILL_QUERY).matches;

    return usesPredictionPillMode(chart) && compactViewport;
};

const ACTIVE_TOUCH_EVENTS = new Set(['touchstart', 'touchmove']);
const END_TOUCH_EVENTS = new Set(['touchend', 'touchcancel']);
const COMPACT_MOUSE_HOVER_EVENT = 'mousemove';
const COMPACT_CLEAR_EVENTS = new Set(['mouseout', 'click']);

const clearLiveChartInteraction = chart => {
    const stateChanged = chart.$touchActive === true ||
        chart.$hoverIndex !== null ||
        chart.$selectedDatasetIndex !== null;

    chart.$touchActive = false;
    chart.$hoverIndex = null;
    chart.$selectedDatasetIndex = null;
    chart.setActiveElements?.([]);
    chart.tooltip?.setActiveElements?.([], {x: 0, y: 0});
    updateCustomLegend(chart, null);

    if (stateChanged) chart.update('none');
};

export const shouldDrawLiveInteraction = chart =>
    !usesCompactPredictionPills(chart) ||
    chart.$touchActive === true ||
    chart.$hoverIndex !== null;

export const getFloatingTagStyle = chart =>
    usesCompactPredictionPills(chart) ? MOBILE_TAG_STYLE : DEFAULT_TAG_STYLE;

export const getFloatingTagItemsForChart = (chart, items) => {
    if (!usesPredictionPillMode(chart)) return items;
    if (usesCompactPredictionPills(chart)) {
        return selectPredictionPills(
            items,
            chart.$selectedDatasetIndex,
            chart.$predictionSelectedDatasetIndex
        );
    }
    return items.map(item => ({
        ...item,
        predictionSelected: item.datasetIndex === chart.$predictionSelectedDatasetIndex
    }));
};

export const formatFloatingTagText = item =>
    `${item.predictionSelected ? '✓ ' : ''}${item.value}% ${item.label}`;

const drawRoundRect = (ctx, x, y, width, height, radius) => {
    const safeRadius = Math.min(radius, height / 2, width / 2);

    ctx.beginPath();
    ctx.moveTo(x + safeRadius, y);
    ctx.lineTo(x + width - safeRadius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
    ctx.lineTo(x + width, y + height - safeRadius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
    ctx.lineTo(x + safeRadius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
    ctx.lineTo(x, y + safeRadius);
    ctx.quadraticCurveTo(x, y, x + safeRadius, y);
    ctx.closePath();
};

const drawTag = (
    ctx,
    {x, top, width, height, text, color: fillColor, selected, predictionSelected},
    style = DEFAULT_TAG_STYLE,
    textColor = '#ffffff'
) => {
    ctx.save();
    ctx.textAlign = 'left';
    ctx.font = style.font;

    ctx.fillStyle = fillColor;
    drawRoundRect(ctx, x, top, width, height, style.radius);
    ctx.fill();
    if (selected || predictionSelected) {
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = style.outlineWidth;
        ctx.stroke();
    }
    ctx.fillStyle = textColor;
    ctx.textBaseline = 'middle';
    ctx.fillText(
        text,
        x + style.paddingX,
        top + height / 2,
        Math.max(0, width - style.paddingX * 2)
    );
    ctx.restore();
};

export const layoutTagItems = (
    items,
    chartArea,
    cursorX,
    align = 'right',
    style = DEFAULT_TAG_STYLE
) => {
    if (!items.length) return [];

    const areaLeft = chartArea.left + TAG_CHART_PADDING;
    const areaRight = chartArea.right - TAG_CHART_PADDING;
    const areaTop = chartArea.top + TAG_CHART_PADDING;
    const areaBottom = chartArea.bottom - TAG_CHART_PADDING;
    const availableHeight = Math.max(style.height, areaBottom - areaTop);
    const maxRows = Math.max(
        1,
        Math.floor((availableHeight + style.gap) / (style.height + style.gap))
    );
    const columnCount = Math.ceil(items.length / maxRows);
    const rowsPerColumn = Math.ceil(items.length / columnCount);
    const availableWidth = Math.max(1, areaRight - areaLeft);
    const widthPerColumn = Math.max(
        1,
        Math.floor((availableWidth - TAG_COLUMN_GAP * (columnCount - 1)) / columnCount)
    );
    const columns = Array.from({length: columnCount}, () => []);

    [...items]
        .sort((first, second) => first.y - second.y)
        .forEach((item, index) => {
            const column = Math.floor(index / rowsPerColumn);
            columns[column].push({
                ...item,
                column,
                width: Math.min(item.width, widthPerColumn),
                height: style.height
            });
        });

    columns.forEach(columnItems => {
        const maxTop = areaBottom - style.height;
        const step = style.height + style.gap;
        const tops = columnItems.map(item =>
            clamp(item.y - style.height / 2, areaTop, maxTop)
        );

        for (let index = 1; index < tops.length; index += 1) {
            tops[index] = Math.max(tops[index], tops[index - 1] + step);
        }

        if (tops[tops.length - 1] > maxTop) {
            tops[tops.length - 1] = maxTop;
            for (let index = tops.length - 2; index >= 0; index -= 1) {
                tops[index] = Math.min(tops[index], tops[index + 1] - step);
            }
        }

        if (tops[0] < areaTop) {
            const shift = areaTop - tops[0];
            tops.forEach((top, index) => { tops[index] = top + shift; });
        }

        columnItems.forEach((item, index) => { item.top = tops[index]; });
    });

    const columnWidths = columns.map(columnItems => Math.max(...columnItems.map(item => item.width)));
    const groupWidth = columnWidths.reduce((sum, width) => sum + width, 0) +
        TAG_COLUMN_GAP * (columnCount - 1);
    const desiredGroupX = align === 'left'
        ? cursorX - TAG_CURSOR_GAP - groupWidth
        : cursorX + TAG_CURSOR_GAP;
    const groupX = clamp(desiredGroupX, areaLeft, areaRight - groupWidth);
    const visualColumnOrder = align === 'left'
        ? columns.map((_, index) => index).reverse()
        : columns.map((_, index) => index);
    let columnX = groupX;

    visualColumnOrder.forEach(columnIndex => {
        columns[columnIndex].forEach(item => { item.x = columnX; });
        columnX += columnWidths[columnIndex] + TAG_COLUMN_GAP;
    });

    return columns.flat();
};

const isLiveChartCanvas = chart =>
    chart.canvas?.getAttribute('data-live-chart-variant') === 'polymarket-multi';

const findLegendContainer = (chart) => {
    let node = chart.canvas.parentElement;
    while (node && node !== document.body) {
        const legend = node.querySelector('.live-tournament-chart__custom-legend');
        if (legend) return legend;
        node = node.parentElement;
    }
    return null;
};

export const appendLegendItem = (legendContainer, itemColor, percentText, name) => {
    const item = document.createElement('div');
    item.className = 'live-tournament-chart__legend-item';

    const text = document.createElement('span');
    text.className = 'live-tournament-chart__legend-text';
    text.style.color = itemColor;

    const percent = document.createElement('span');
    percent.className = 'live-tournament-chart__legend-percent';
    percent.textContent = percentText;

    const nameSpan = document.createElement('span');
    nameSpan.className = 'live-tournament-chart__legend-name';
    nameSpan.textContent = name;
    nameSpan.title = name;

    text.append(percent, nameSpan);
    item.appendChild(text);
    legendContainer.appendChild(item);
};

export const updateCustomLegend = (chart, hoverIndex) => {
    const legendContainer = chart.$legendContainer || findLegendContainer(chart);
    if (!legendContainer) return;

    legendContainer.innerHTML = '';

    chart.data.datasets.forEach(dataset => {
        const dataLength = dataset.data.length;
        const valueIndex = hoverIndex !== null && hoverIndex !== undefined ? hoverIndex : dataLength - 1;
        const value = dataset.data[valueIndex];
        appendLegendItem(legendContainer, dataset.borderColor, `${value}%`, dataset.label);
    });
};

export const createCustomLegend = (parent) => {
    const legend = document.createElement('div');
    legend.className = 'live-tournament-chart__custom-legend';
    parent.appendChild(legend);
    return legend;
};

// Дописанным точкам Chart.js создаёт элементы в режиме 'reset': LineController
// ставит им y = vScale.getBasePixel(), то есть низ области. Следующий, уже
// анимированный проход едет от этого низа к настоящему значению, и новый тик
// выползает снизу вместо того, чтобы появиться на своём месте.
// Между двумя проходами есть окно — beforeDatasetUpdate: шкалы уже пересчитаны
// под новое количество меток, анимации ещё не созданы. Сажаем в нём новые точки
// на конечные пиксели: анимация получается from === to и не двигает их, а старые
// точки по-прежнему переезжают плавно.
// Снимок длин обязателен: сама по себе точка «дописанная» и «просто стоящая на
// базовом пикселе» (null в данных) неразличимы.
const APPENDED_POINTS_KEY = '$appendedPointStarts';

export const markAppendedPoints = chart => {
    chart[APPENDED_POINTS_KEY] = chart.data.datasets.map(
        (dataset, datasetIndex) => chart.getDatasetMeta(datasetIndex).data.length
    );
};

export const clearAppendedPoints = chart => {
    chart[APPENDED_POINTS_KEY] = null;
};

export const settleAppendedPoints = (chart, meta, datasetIndex) => {
    const starts = chart[APPENDED_POINTS_KEY];
    if (!Array.isArray(starts)) return 0;

    const start = starts[datasetIndex];
    const points = meta && meta.data;
    const values = chart.data.datasets[datasetIndex]?.data;
    if (!Number.isInteger(start)) return 0;
    if (!Array.isArray(points) || !Array.isArray(values)) return 0;
    if (!meta.iScale || !meta.vScale) return 0;

    let settled = 0;
    for (let index = start; index < points.length; index += 1) {
        const point = points[index];
        const value = values[index];
        // Дырку в данных не трогаем: базовый пиксель для неё и есть конечное место.
        if (!point || !isFiniteNumber(value)) continue;

        point.x = meta.iScale.getPixelForValue(index, index);
        point.y = meta.vScale.getPixelForValue(value, index);
        settled += 1;
    }

    return settled;
};

const liveChartPlugin = {
    id: 'liveChartPlugin',
    beforeInit(chart) {
        // Enable high-quality rendering
        const ctx = chart.ctx;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Initialize hover state
        chart.$hoverIndex = null;
        chart.$selectedDatasetIndex = null;
        chart.$predictionSelectedDatasetIndex = null;
        chart.$touchActive = false;
    },
    beforeDatasetUpdate(chart, args) {
        settleAppendedPoints(chart, args.meta, args.index);
    },
    afterEvent(chart, args) {
        if (!isLiveChartCanvas(chart)) return;

        const compactPredictionPills = usesCompactPredictionPills(chart);
        const nativeEventType = args.event?.native?.type;

        if (compactPredictionPills) {
            if (END_TOUCH_EVENTS.has(nativeEventType)) {
                clearLiveChartInteraction(chart);
                return;
            }

            if (ACTIVE_TOUCH_EVENTS.has(nativeEventType)) {
                chart.$touchActive = true;
            } else if (nativeEventType === COMPACT_MOUSE_HOVER_EVENT) {
                chart.$touchActive = false;
            } else {
                if (COMPACT_CLEAR_EVENTS.has(nativeEventType)) {
                    clearLiveChartInteraction(chart);
                }
                return;
            }
        }

        const activeElements = chart.tooltip?._active || [];

        if (activeElements.length > 0) {
            const newIndex = activeElements[0].index;
            const selectedDatasetIndex = compactPredictionPills
                ? findClosestDatasetIndex(
                    activeElements.map(activeElement => ({
                        datasetIndex: activeElement.datasetIndex,
                        y: chart.getDatasetMeta(activeElement.datasetIndex).data[newIndex]?.y
                    })),
                    args.event?.y
                )
                : null;
            const hoverChanged = chart.$hoverIndex !== newIndex;
            const selectionChanged = chart.$selectedDatasetIndex !== selectedDatasetIndex;

            chart.$hoverIndex = newIndex;
            chart.$selectedDatasetIndex = selectedDatasetIndex;
            if (hoverChanged) updateCustomLegend(chart, newIndex);
            if (hoverChanged || selectionChanged) chart.update('none');
        } else if (chart.$hoverIndex !== null || chart.$selectedDatasetIndex !== null) {
            chart.$hoverIndex = null;
            chart.$selectedDatasetIndex = null;
            updateCustomLegend(chart, null);
            chart.update('none');
        }
    },
    afterDraw(chart) {
        if (!isLiveChartCanvas(chart)) return;
        if (!shouldDrawLiveInteraction(chart)) return;
        if (!chart.tooltip?._active?.length) return;

        const {ctx, chartArea} = chart;
        const activeElements = chart.tooltip._active;
        const x = chart.tooltip.caretX;
        const dataIndex = activeElements[0]?.index;

        ctx.save();

        // Ensure anti-aliasing is enabled for custom drawing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Draw vertical crosshair
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
        ctx.setLineDash([]);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, chartArea.top);
        ctx.lineTo(x, chartArea.bottom);
        ctx.stroke();

        // Draw timestamp at top of crosshair
        const timestamp = chart.data.labels[dataIndex];
        if (timestamp) {
            ctx.font = '12px Arial, sans-serif';
            ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            ctx.fillText(timestamp, x, chartArea.top - 6);
        }

        // Draw points on lines at intersection
        chart.data.datasets.forEach((dataset, index) => {
            const meta = chart.getDatasetMeta(index);
            const point = meta.data[dataIndex];
            if (!point) return;

            ctx.fillStyle = dataset.borderColor;
            ctx.beginPath();
            ctx.arc(point.x, point.y, 4, 0, Math.PI * 2);
            ctx.fill();

            // White outline for better visibility
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 2;
            ctx.stroke();
        });

        // Draw floating pills
        if (dataIndex !== undefined && dataIndex >= 0) {
            const labelItems = [];

            chart.data.datasets.forEach((dataset, index) => {
                const meta = chart.getDatasetMeta(index);
                const point = meta.data[dataIndex];
                if (!point) return;

                const value = dataset.data[dataIndex];
                labelItems.push({
                    datasetIndex: index,
                    color: dataset.borderColor,
                    label: dataset.label,
                    value: value,
                    y: point.y
                });
            });

            // Determine alignment based on cursor position
            const chartCenter = chartArea.left + chartArea.width / 2;
            const align = x > chartCenter ? 'left' : 'right';

            const tagStyle = getFloatingTagStyle(chart);
            ctx.font = tagStyle.font;
            const visibleItems = getFloatingTagItemsForChart(chart, labelItems);
            const measuredItems = visibleItems.map(item => {
                const text = formatFloatingTagText(item);
                return {
                    ...item,
                    text,
                    width: Math.min(
                        ctx.measureText(text).width + tagStyle.paddingX * 2,
                        chartArea.right - chartArea.left - TAG_CHART_PADDING * 2
                    )
                };
            });

            layoutTagItems(measuredItems, chartArea, x, align, tagStyle)
                .forEach(item => drawTag(ctx, item, tagStyle));
        }

        ctx.restore();
    },
    beforeDestroy(chart) {
        chart.$predictionSelectionCleanup?.();
        chart.$predictionSelectionCleanup = null;
    }
};

Chart.register(liveChartPlugin);

export const buildDataset = (values, label, lineColor, options = {}) => {
    // Parse RGB from hex color for transparency
    const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : {r: 255, g: 255, b: 255};
    };

    const rgb = hexToRgb(lineColor);

    return {
        label,
        data: values,
        borderColor: lineColor,
        backgroundColor: 'transparent',
        pointBackgroundColor: lineColor,
        pointBorderColor: lineColor,
        borderWidth: options.borderWidth || 2,
        fill: false,
        tension: options.tension ?? 0.35,
        cubicInterpolationMode: options.cubicInterpolationMode || 'default',
        pointRadius: options.pointRadius ?? 0,
        pointHoverRadius: options.pointHoverRadius ?? 4,
        segment: {
            borderColor: (ctx) => {
                const chart = ctx.chart;
                const hoverIndex = chart.$hoverIndex;

                // If no hover, use default color
                if (hoverIndex === null || hoverIndex === undefined) {
                    return lineColor;
                }

                // If segment starts after hover point, make it semi-transparent
                if (ctx.p0DataIndex >= hoverIndex) {
                    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3)`;
                }

                return lineColor;
            }
        }
    };
};

export const sharedOptions = ({
    legend = true,
    grid = true,
    ticks = true,
    min = 0,
    max = 100,
    yPosition = 'left',
    xTickLimit = 5,
    yTickLimit = 5,
    xGridColor = 'rgba(255, 255, 255, 0.045)',
    yGridColor = 'rgba(255, 255, 255, 0.06)',
    layoutPadding = 0
} = {}) => ({
    responsive: true,
    maintainAspectRatio: false,
    devicePixelRatio: Math.max(2, window.devicePixelRatio || 1),
    layout: {
        padding: layoutPadding
    },
    animation: {
        duration: 350
    },
    interaction: {
        mode: 'index',
        intersect: false
    },
    scales: {
        x: {
            grid: {
                display: grid,
                color: xGridColor
            },
            ticks: {
                display: ticks,
                maxTicksLimit: xTickLimit,
                get color() {
                    return color('--main-color');
                }
            },
            border: {
                display: grid,
                get color() {
                    return color('--accent-background');
                }
            }
        },
        y: {
            min,
            max,
            position: yPosition,
            grid: {
                display: grid,
                color: yGridColor
            },
            ticks: {
                display: ticks,
                maxTicksLimit: yTickLimit,
                stepSize: 25,
                get color() {
                    return color('--main-color');
                },
                callback: value => `${value}%`
            },
            border: {
                display: grid,
                get color() {
                    return color('--accent-background');
                }
            }
        }
    },
    plugins: {
        legend: {
            display: false
        },
        tooltip: {
            enabled: true,
            mode: 'index',
            intersect: false,
            backgroundColor: 'rgba(0, 0, 0, 0)',
            titleColor: 'rgba(0, 0, 0, 0)',
            bodyColor: 'rgba(0, 0, 0, 0)',
            borderColor: 'rgba(0, 0, 0, 0)',
            borderWidth: 0,
            displayColors: false,
            padding: 0,
            caretSize: 0,
            callbacks: {
                label: () => '',
                title: () => ''
            }
        }
    }
});
